import type { PlanRes } from '../res'
import * as dateLib from './date_lib'

export type Plan = {
  id?: number
  startDate?: string
  endDate?: string | null
}

export type PlanLike = PlanRes | Plan

// Helper function to normalize dates to strings for comparison
const normalizeDate = (date: string | Date): string => {
  if (date instanceof Date) {
    return dateLib.formatDate(date)
  }
  return date
}

/**
 * 現在のプランかどうか（実行中）
 * @param plan - プランオブジェクト
 * @param date - 基準日（nullの場合は今日）
 */
export const isCurrent = (plan: PlanLike, date: string | null): boolean => {
  if (!plan.startDate) return false
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(plan.startDate)
  if (startDate > baseDate) return false
  if (!plan.endDate) return true
  const endDate = normalizeDate(plan.endDate)
  return endDate >= baseDate
}

/**
 * 過去のプランかどうか（終了済み）
 * @param plan - プランオブジェクト
 * @param date - 基準日（nullの場合は今日）
 */
export const isPast = (plan: PlanLike, date: string | null): boolean => {
  if (!plan.startDate) return true
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(plan.startDate)
  if (startDate > baseDate) return false
  if (!plan.endDate) return false
  const endDate = normalizeDate(plan.endDate)
  return endDate < baseDate
}

/**
 * 未来のプランかどうか（開始予定）
 * @param plan - プランオブジェクト
 * @param date - 基準日（nullの場合は今日）
 */
export const isFuture = (plan: PlanLike, date: string | null): boolean => {
  if (!plan.startDate) return false
  const baseDate = date ?? dateLib.getToday()
  const startDate = normalizeDate(plan.startDate)
  return startDate > baseDate
}

/**
 * 指定月にプランがアクティブかどうかを判定
 * @param plan - プランオブジェクト
 * @param month - YYYY-MM形式の月文字列
 * @returns 指定月にアクティブな場合はtrue
 */
export const isMonth = (plan: PlanLike, month: string): boolean => {
  // 指定月の開始日と終了日を取得（YYYY-MM-DD形式の文字列）
  const monthStart = dateLib.getMonthStart(month)
  const monthEnd = dateLib.getMonthEnd(month)

  if (!plan.startDate) return false
  const planStart = normalizeDate(plan.startDate)
  const planEnd = plan.endDate ? normalizeDate(plan.endDate) : null

  // プラン期間が指定月と重複しているか確認
  return planStart <= monthEnd && (!planEnd || planEnd >= monthStart)
}

/**
 * 指定月にプランが請求対象かどうかを判定（初月は無料のため除外）
 * @param plan - プランオブジェクト
 * @param month - YYYY-MM形式の月文字列
 * @returns 指定月が請求対象の場合はtrue、初月の場合はfalse
 */
export const isInvoice = (plan: PlanLike, month: string): boolean => {
  // まずアクティブかどうかを確認
  if (!isMonth(plan, month)) {
    return false
  }

  if (!plan.startDate) {
    return false
  }

  const planStart = normalizeDate(plan.startDate)

  // 指定月の年月を取得（YYYY-MM形式）
  const targetYearMonth = month.substring(0, 7) // YYYY-MM

  // プラン開始月の年月を取得（YYYY-MM形式）
  const planStartYearMonth = dateLib.getYearMonth(planStart)

  // 指定月がプラン開始月と同じ場合は初月なのでfalse（無料）
  if (targetYearMonth === planStartYearMonth) {
    return false
  }

  // 初月でない場合はtrue（請求対象）
  return true
}

/**
 * 現在のプランで継続中かどうか（実行中でendDateがない）
 * @param plan - プランオブジェクト
 * @param date - 基準日（nullの場合は今日）
 * @returns 現在のプランで継続中の場合はtrue
 */
export const isCurrentAndContinue = (
  plan: PlanLike,
  date: string | null,
): boolean => {
  return isCurrent(plan, date) && plan.endDate === null
}

/**
 * 現在のプランで終了予定かどうか（実行中でendDateがある）
 * @param plan - プランオブジェクト
 * @param date - 基準日（nullの場合は今日）
 * @returns 現在のプランで終了予定の場合はtrue
 */
export const isCurrentAndStop = (
  plan: PlanLike,
  date: string | null,
): boolean => {
  return isCurrent(plan, date) && plan.endDate !== null
}

/**
 * キャンセル可能かどうか（endDateがnullの場合のみ）
 * @param plan - プランオブジェクト
 * @returns キャンセル可能な場合はtrue
 */
export const canCancel = (plan: PlanLike): boolean => {
  return plan.endDate === null
}

/**
 * 復活可能かどうか（endDateがnullでない場合のみ）
 * @param plan - プランオブジェクト
 * @returns 復活可能な場合はtrue
 */
export const canRevival = (plan: PlanLike): boolean => {
  return plan.endDate !== null
}

/**
 * 削除可能かどうか（startDateが今日より未来の場合のみ）
 * @param plan - プランオブジェクト
 * @returns 削除可能な場合はtrue
 */
export const canDelete = (plan: PlanLike): boolean => {
  if (!plan.startDate) return false
  const today = dateLib.getToday()
  const startDate = normalizeDate(plan.startDate)
  return startDate > today
}
